import { defineStore } from "pinia";
import { computed, ComputedRef, ref } from "vue";

interface DecodedData {
  exp: number; // Expiration time (UNIX timestamp)
  iat: number; // Issued at time (UNIX timestamp)
  auth_time: number; // Authentication time (UNIX timestamp)
  jti: string; // JWT ID
  iss: string; // Issuer
  aud: string[]; // Audience
  sub: string; // Subject (User ID)
  typ: string; // Token type
  azp: string; // Authorized party
  nonce: string; // Nonce
  session_state: string; // Session state
  acr: string; // Authentication Context Class Reference
  "allowed-origins": string[]; // Allowed origins
  realm_access: {
    roles: string[]; // Roles for the realm
  };
  resource_access: {
    [resource: string]: {
      roles: string[]; // Roles for each resource
    };
  };
  scope: string; // Scope of access
  sid: string; // Session ID
  email_verified: boolean; // Email verification status
  LDAP_ENTRY_DN: string; // LDAP Distinguished Name
  name: string; // Full name
  preferred_username: string; // Preferred username
  given_name: string; // Given name
  family_name: string; // Family name
  LDAP_ID: string; // LDAP ID
  brand?: string;
}

interface AuthUserProfile {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  userId?: string;
  decodedData?: DecodedData;
  serviceType?: string;
}

interface AuthModuleState {
  isAuthenticated?: boolean;
  userProfile?: AuthUserProfile;
  accessToken?: string;
  requestHeader?: object;
  logoUrl?: string;
}

export const useAuthStore = defineStore("auth", () => {
  // STATES
  const userProfile = ref<AuthModuleState["userProfile"]>({});
  const isAuthenticated = ref<AuthModuleState["isAuthenticated"]>(false);
  const accessToken = ref<AuthModuleState["accessToken"]>();
  const requestHeader = ref<AuthModuleState["requestHeader"]>();
  const logoUrl = ref<AuthModuleState["logoUrl"]>();

  // ACTIONS
  const resetAuthState = () => {
    userProfile.value = {};
    isAuthenticated.value = false;
    requestHeader.value = {};
    accessToken.value = "";
    logoUrl.value = "";
  };

  const setAuthUserProfile = (payload: AuthModuleState["userProfile"]) => {
    userProfile.value = { ...payload };
  };

  const setLogoURL = (payload: AuthModuleState["logoUrl"]) => {
    logoUrl.value = payload;
  };

  const loginApp = (payload: AuthModuleState) => {
    isAuthenticated.value = true;
    accessToken.value = payload.accessToken;
    requestHeader.value = {
      headers: {
        Authorization: "Bearer " + payload.accessToken,
      },
    };
  };

  // GETTERS
  const getAccessToken: ComputedRef<string | undefined> = computed(
    () => accessToken.value,
  );
  const getLogoURL: ComputedRef<string | undefined> = computed(
    () => logoUrl.value,
  );
  const getUserProfile: ComputedRef<AuthUserProfile> = computed(
    () => userProfile.value ?? {},
  );
  const getRequestHeader: ComputedRef<object | undefined> = computed(
    () => requestHeader.value,
  );

  return {
    getAccessToken,
    getLogoURL,
    getUserProfile,
    getRequestHeader,
    resetAuthState,
    setAuthUserProfile,
    setLogoURL,
    loginApp,
  };
});
